import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Table } from '../components'
import { RNCarriers, RNLogging } from '../helpers/routeNames'
import { CellCols } from '../helpers/cellColumns'
import { carrierStore } from '../store/carrier'
import { sensorStore } from '../store/sensor'
import { Link, useNavigate } from 'react-router-dom'
import { Badge, Select, Space, Tag, Tooltip } from 'antd'
import { fetchCarrierTypes } from '../api/inventory'
import { CarrierType } from '../helpers/types/inventory'

const Carriers: FC = () => {
	const { t } = useTranslation()
	const { carriers } = carrierStore()
	const { sensors } = sensorStore()
	const navigate = useNavigate()

	const [carrierTypes, setCarrierTypes] = useState<CarrierType[]>([])
	const [typeFilter, setTypeFilter] = useState<number | null>(null)

	useEffect(() => {
		fetchCarrierTypes()
			.then(setCarrierTypes)
			.catch(() => {})
	}, [])

	const formatLastSeenAt = (value: string | null) => {
		if (!value) return t('NoData')

		const date = new Date(value)
		if (Number.isNaN(date.getTime())) return t('NoData')

		return date.toLocaleString('ru')
	}

	const hasSideNetwPult = (carrierId: number) => {
		return (
			sensors.find(
				(s) =>
					s.code === 'side_netw_pult' &&
					s.sensorData[0]?.value === '1' &&
					s.sensorData[0]?.carrierId === Number(carrierId)
			) !== undefined
		)
	}

	const columns = CellCols(RNCarriers, [
		{
			title: t('Status'),
			dataIndex: 'status',
			key: 'status',
			render: (_: any, a: any) => (
				<Tooltip
					title={t(
						a.status === 'online' ? 'Online' : 'Offline'
					)}>
					<Badge
						status={
							a.status === 'online'
								? 'success'
								: 'default'
						}
					/>
				</Tooltip>
			)
		},
		{
			title: t('Name'),
			dataIndex: 'name',
			key: 'name',
			render: (text: string, a: any) => (
				<Space size={8}>
					<Link to={`${RNCarriers}/${a.id}`}>{text}</Link>
					{/* Carrier type badge */}
						{(a as any).carrierType && (
						<Tag color="geekblue" style={{ marginInlineEnd: 0 }}>
								{(a as any).carrierType.name}
						</Tag>
					)}
					{hasSideNetwPult(a.id) ? (
						<Tooltip
							title={t('NetworkPanelDisconnected')}>
							<Badge status="warning" />
						</Tooltip>
					) : null}
					{a.activeProblemsCount > 0 ? (
						<Link
							to={`${RNLogging}/${a.id}?onlyErrors=true`}
							onClick={(event) =>
								event.stopPropagation()
							}
							aria-label={`${text} errors`}>
							<Badge
								count={a.activeProblemsCount}
								style={{ backgroundColor: '#ff4d4f' }}
							/>
						</Link>
					) : null}
				</Space>
			)
		},
		{
			title: t('FactoryNumber'),
			dataIndex: 'factoryNumber',
			key: 'factoryNumber'
		},
		{
			title: t('LastSeenAt'),
			dataIndex: 'lastSeenAt',
			key: 'lastSeenAt',
			render: (value: string | null) => (
				<Tooltip
					title={
						value
							? new Date(value).toISOString()
							: t('NoData')
					}>
					<span>{formatLastSeenAt(value)}</span>
				</Tooltip>
			)
		},
		{
			title: t('IsDeleted'),
			dataIndex: 'isDeleted',
			key: 'isDeleted',
			render: (text: boolean) => <>{t(text ? 'Yes' : 'No')}</>
		}
	])

	const [searchQuery, setSearchQuery] = useState('')

	const filtered = carriers.filter((el) => {
		const matchesSearch = searchQuery
			? el.name.toLowerCase().includes(searchQuery.toLowerCase())
			: true
		const matchesType = typeFilter
			? (el as any).carrierTypeId === typeFilter
			: true
		return matchesSearch && matchesType
	})

	const onSearch = (value: string) => {
		setSearchQuery(value)
	}

	const onAdd = () => {
		navigate(RNCarriers + '/new')
	}

	const onRefresh = () => {}

	return (
		<div className="carriers">
			<header className="page-header">
				<h1 className="page-h1">{t('Carriers')}</h1>
			</header>
			<main>
				{/* Type filter toolbar */}
				{carrierTypes.length > 0 && (
					<div style={{ padding: '0 0 12px' }}>
						<Select
							allowClear
							placeholder="All types"
							style={{ width: 200 }}
							value={typeFilter}
							onChange={(v) => setTypeFilter(v ?? null)}
							options={carrierTypes.map((ct) => ({
								label: ct.name,
								value: ct.id
							}))}
						/>
					</div>
				)}
				<Table
					dataSource={filtered}
					columns={columns}
					onSearch={onSearch}
					onAdd={onAdd}
					onRefresh={onRefresh}
				/>
			</main>
		</div>
	)
}

export default Carriers

